// In-memory fake Daikin backend for local development.
//
// Enabled by REACT_APP_DAIKIN_MOCK="true" (set in .env.development), so it's on
// when running `npm start` locally and OFF in the deployed build (the deploy
// workflow never sets that flag). When on, DaikinControlCard talks to these
// functions instead of AppSync, so you can exercise the UI without the backend
// deployed or the OAuth token seeded.

// Mirrors the real account: four dx23 units, each independently controllable.
let units = [
    mkUnit('mock-unit-1', 'Living Room', 21, 23),
    mkUnit('mock-unit-2', 'Bedroom', 21, 25),
    mkUnit('mock-unit-3', 'Office', 20, 22),
    mkUnit('mock-unit-4', 'Kitchen', 21, 26),
];
let callsRemaining = 150;

function mkUnit(device_id, name, room, outdoor) {
    return {
        device_id,
        name,
        model: 'dx23',
        online: true,
        power: 'off',
        operation_mode: 'heating',
        setpoint: 18,
        setpoint_min: 16,
        setpoint_max: 32,
        setpoint_step: 0.5,
        fan_mode: 'fixed',
        fan_speed: 3,
        fan_speed_max: 3,
        room_temperature: room,
        outdoor_temperature: outdoor,
        schedule_enabled: false,
        holiday_mode: false,
        mode_conflict: false,
        error_state: false,
        error_code: '00',
    };
}

// Mimic the backend's cache-vs-live behaviour: a read inside the debounce
// window returns cached data for free; otherwise it "spends" a call.
let lastLiveFetch = 0;
const MIN_REFRESH_MS = 5 * 60 * 1000;

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

// Mock automation objective (what the backend would be driving toward). Idle/off
// by default; manually switching a unit on then shows as "overridden" vs this.
const objective = {
    power: 'off', setpoint: null, target: null, phase: null, measured: null, measured_at: null,
};

function autoState(u) {
    if (!objective.power) return null;
    const onOk = u.power === objective.power;
    const spOk = objective.setpoint == null || u.setpoint === objective.setpoint;
    return onOk && spOk ? 'accepted' : 'overridden';
}

function envelope(fromCache, conflict = null) {
    return {
        device_count: units.length,
        units: units.map((u) => ({ ...u, auto_state: autoState(u) })),
        objective: { ...objective },
        from_cache: fromCache,
        stale: false,
        fetched_at: new Date().toISOString(),
        calls_remaining: callsRemaining,
        conflict,
    };
}

// The mock is the only writer, so live state always matches what the user saw —
// the stale-write guard never trips here. (Real backend re-reads Daikin first.)

export async function mockGetStatus() {
    await delay(250);
    const now = Date.now();
    if (now - lastLiveFetch < MIN_REFRESH_MS && lastLiveFetch > 0) {
        return envelope(true); // served from cache, no call spent
    }
    lastLiveFetch = now;
    callsRemaining = Math.max(0, callsRemaining - 1);
    // Drift room temps a touch so successive refreshes look alive.
    units = units.map((u) => ({
        ...u,
        room_temperature: Math.round((19 + Math.random()) * 10) / 10,
    }));
    return envelope(false);
}

function mutate(device_id, patch) {
    units = units.map((u) => (u.device_id === device_id ? { ...u, ...patch } : u));
    callsRemaining = Math.max(0, callsRemaining - 1);
    lastLiveFetch = Date.now();
    return envelope(false);
}

export async function mockSetPower(device_id, value /*, expected */) {
    await delay(400);
    return mutate(device_id, { power: value });
}

export async function mockSetSetpoint(device_id, value /*, expected */) {
    await delay(400);
    const u = units.find((x) => x.device_id === device_id);
    const step = u?.setpoint_step || 0.5;
    const clamped = Math.min(u?.setpoint_max ?? 32, Math.max(u?.setpoint_min ?? 16, Math.round(value / step) * step));
    return mutate(device_id, { setpoint: clamped });
}

export async function mockSetFanSpeed(device_id, value /*, expected */) {
    await delay(400);
    const u = units.find((x) => x.device_id === device_id);
    const top = u?.fan_speed_max ?? 3;
    return mutate(device_id, { fan_mode: 'fixed', fan_speed: Math.min(top, Math.max(1, Math.round(value))) });
}

export const DAIKIN_MOCK = process.env.REACT_APP_DAIKIN_MOCK === 'true';
